import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'flowbite-react';
import { useState } from 'react';
import { FiLock } from 'react-icons/fi';
import { SecretBundlePanel, type SecretFieldSpec } from './SecretBundlePanel.js';

interface Props {
  /** Same as `SecretBundlePanel.bundlePath` — API path up to the bundle root. */
  bundlePath: string;
  /** Field spec list passed straight through to `SecretBundlePanel`. */
  fields: readonly SecretFieldSpec[];
  /** Modal title — usually the row's display name + "secrets". */
  title: string;
  /** Optional subtitle shown inside the panel. */
  subtitle?: string;
  /** Aria-label for the trigger button (announce the row context). */
  ariaLabel: string;
}

/**
 * Per-row "manage secrets" trigger. Renders an icon button that opens
 * a modal hosting `<SecretBundlePanel>`. Used by Licenses / OAuthApps
 * / WorkfrontConnections / FusionConnections lists, where the bundle
 * is per-row (the GitHubOptions tab is a single Client bundle and
 * embeds the panel directly).
 */
export function SecretsRowButton({
  bundlePath,
  fields,
  title,
  subtitle,
  ariaLabel,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={ariaLabel}
        title="Manage secrets"
        className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
      >
        <FiLock size={16} />
      </button>
      {open ? (
        <Modal show={true} onClose={() => setOpen(false)} size="lg" dismissible>
          <ModalHeader>{title}</ModalHeader>
          <ModalBody>
            <SecretBundlePanel
              bundlePath={bundlePath}
              fields={fields}
              title="Bundle fields"
              subtitle={subtitle}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="gray" onClick={() => setOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      ) : null}
    </>
  );
}
